import { eventBus } from '@atc/cai-event-bus';
import WalletStorageService from '../services/WalletStorageService/WalletStorageService.js';
import DealerDealService from '../services/DealerDealService/DealerDealService.js';
import PayoffQuoteService from '../services/PayoffQuoteService/PayoffQuoteService.js';
import VrsService from '../services/VrsService/VrsService.js';
import hydrateWallet from '../helpers/hydrateWalletPaymentsInfo.js';
import updatePaymentsInfo from '../helpers/updatePaymentsInfo.js';
import getInventoryPayments from '../helpers/getInventoryPayments.js';
import updateDataAfterLogin from '../helpers/updateDataAfterLogin.js';
import getFormattedTime from '../helpers/getFormattedTime.js';
import buildFetchers from '../fetchers/utils/buildFetchers.js';
import { UDS_OPEN_SDP_REQUEST, UDS_CONFIG_UPDATE_REQUEST, UDS_CONFIG_REQUEST, WALLET_CREATE_ID_REQUEST, WALLET_GET_INVENTORY_PAYMENTS_REQUEST, WALLET_VRS_VEHICLE_VALUATION_REQUEST, WALLET_VRS_OPTIONS_REQUEST, WALLET_PAYOFF_QUOTE_SERVICE_SUBMIT_REQUEST, WALLET_PAYOFF_QUOTE_SERVICE_SUBMIT_COMPLETED, WALLET_PAYOFF_QUOTE_SERVICE_GET_SELECTED_LENDER_REQUEST, WALLET_PAYOFF_QUOTE_SERVICE_GET_SELECTED_LENDER_COMPLETED, WALLET_PAYOFF_QUOTE_SERVICE_GET_LENDERS_REQUEST, WALLET_PAYOFF_QUOTE_SERVICE_GET_LENDERS_COMPLETED, WALLET_GET_DEALS_WITH_DEALER_INFORMATION_REQUEST, WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST, WALLET_RESET_DEAL_NOTIFICATION_REQUEST, WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST, WALLET_GET_DEAL_DETAILS_REQUEST, UDS_PENCILING_DATA_UPDATED, WALLET_UPDATE_PAYMENTS_INFO_REQUEST, WALLET_UPDATE_WALLET_STORAGE_REQUEST, WALLET_UPDATE_DATA_AFTER_LOGIN_REQUEST, WALLET_RESET_CONSUMER_ID_REQUEST, WALLET_GET_ICO_TRADE_INS_REQUEST, WALLET_GET_KBB_TRADE_INS_REQUEST, WALLET_ENSURE_PAYMENTS_INFO_REQUEST, WALLET_GET_INITIALIZATION_STATUS_REQUEST, WALLET_GET_PAYMENTS_INFORMATION_REQUEST, WALLET_ENSURE_PAYMENTS_INFO_COMPLETED, WALLET_GET_PAYMENTS_INFORMATION_COMPLETED, WALLET_GET_INITIALIZATION_STATUS_COMPLETED, WALLET_SET_ERROR_ALERT_REQUEST, WALLET_UPDATE_PAYMENTS_INFO_COMPLETED, WALLET_GET_KBB_TRADE_INS_COMPLETED, WALLET_UPDATE_WALLET_STORAGE_COMPLETED, WALLET_GET_DEAL_DETAILS_COMPLETED, WALLET_GET_DEALS_WITH_DEALER_INFORMATION_COMPLETED, WALLET_GET_ICO_TRADE_INS_COMPLETED, WALLET_VRS_OPTIONS_COMPLETED, WALLET_VRS_VEHICLE_VALUATION_COMPLETED, WALLET_CREATE_ID_COMPLETED, UDS_CONFIG_COMPLETED, UDS_CONFIG_UPDATE_COMPLETED } from '../constants/events.js';
import buildPaymentsInfoForWalletManager from '../helpers/buildPaymentsInfoForWalletManager.js';
import buildUDSConfigForWalletManager from '../helpers/buildUDSConfigForWalletManager.js';
import debugWallet from '../helpers/debugWallet.js';
import buildKbbTradeIns from '../helpers/buildKbbTradeIns.js';
import updateWalletStorage from '../helpers/updateWalletStorage.js';
import buildDealerInfomation from '../helpers/buildDealerInfomation.js';
import getIcoOffers from '../helpers/getIcoOffers.js';
import getVrsReferenceData from '../helpers/getVrsReferenceData.js';
import validatePaymentsInfo from '../helpers/validatePaymentsInfo.js';
import createWalletId from '../helpers/createWalletId.js';
import buildCalculatorConfig from '../CalculatorConfiguration/buildCalculatorConfig.js';
import modifyExternalConfig from '../CalculatorConfiguration/modifyExternalConfig.js';
import updateCalculatorConfig from '../CalculatorConfiguration/updateCalculatorConfig.js';
import buildInitObject from '../helpers/buildInitObject.js';
import digitalOverlayHelpers from '../helpers/digitalOverlayHelpers.js';

/**
 * @module WalletManager
 * @description A centralized manager for wallet services that provides a unified interface to handle
 * payments, trade-ins, deals, payoff quotes, and other wallet-related functionalities. The WalletManager
 * coordinates between different services and responds to events from the application.
 */
class WalletManager {
  /**
   * Creates an instance of WalletManager
   * @constructor
   * @param {Object} options - Configuration options
   * @param {String} options.environment - Environment setting (staging/prod) for service endpoints
   * @param {Object} [options.fetchers] - Custom fetchers to override default data fetching implementations
   * @param {Object} [options.walletDefaults] - Default wallet configuration values
   */
  constructor() {
    let {
      environment,
      fetchers,
      walletDefaults
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    /**
     * Unique identifier for the wallet
     * @type {String|null}
     */
    this.walletId = null;

    /**
     * Environment setting for services (staging/prod)
     * @type {String}
     */
    this.environment = environment;

    /**
     * Default wallet configuration values
     * @type {Object}
     */
    this.walletDefaults = walletDefaults;

    /**
     * Service for managing wallet storage operations
     * @type {WalletStorageService}
     */
    this.walletStorageService = new WalletStorageService({
      environment
    });

    /**
     * Service for managing dealer deal operations
     * @type {DealerDealService}
     */
    this.dealerDealService = new DealerDealService({
      environment
    });

    /**
     * Service for managing payoff quote operations
     * @type {PayoffQuoteService}
     */
    this.payoffQuoteService = new PayoffQuoteService({
      environment
    });

    /**
     * Service for VRS (Vehicle Reference System) operations
     * @type {VrsService}
     */
    this.vrsService = new VrsService({
      environment
    });

    /**
     * Current payment information data
     * @type {Object}
     */
    this.paymentsInfo = {};

    /**
     * Data fetchers used by the wallet manager
     * @type {Object}
     */
    this.fetchers = {
      ...buildFetchers(this.environment),
      ...fetchers
    };

    /**
     * Calculator configuration data
     * @type {Object}
     */
    this.calcConfig = {};

    /**
     * Deal details data
     * @type {Object}
     */
    this.dealDetails = {};

    // add event listeners
    this.connectEventBus({
      [WALLET_GET_PAYMENTS_INFORMATION_REQUEST]: this.getWalletPaymentsInformation.bind(this),
      [WALLET_GET_INITIALIZATION_STATUS_REQUEST]: this.getInitializationStatus.bind(this),
      [WALLET_ENSURE_PAYMENTS_INFO_REQUEST]: this.loadPaymentsInfo.bind(this),
      [WALLET_GET_KBB_TRADE_INS_REQUEST]: this.getTradeIns.bind(this),
      [WALLET_GET_ICO_TRADE_INS_REQUEST]: this.handleGetIcoTradeIns.bind(this),
      [WALLET_RESET_CONSUMER_ID_REQUEST]: this.walletStorageService.resetConsumerid.bind(this.walletStorageService),
      [WALLET_UPDATE_DATA_AFTER_LOGIN_REQUEST]: updateDataAfterLogin.bind(this, this.walletStorageService),
      [WALLET_UPDATE_WALLET_STORAGE_REQUEST]: this.updateWalletStorageWithData.bind(this),
      [WALLET_UPDATE_PAYMENTS_INFO_REQUEST]: this.handlePaymentsInfoUpdate.bind(this),
      [UDS_PENCILING_DATA_UPDATED]: this.updatePencilingOfferPrice.bind(this),
      [WALLET_GET_DEAL_DETAILS_REQUEST]: this.getDealDetails.bind(this),
      [WALLET_GET_DEAL_DETAILS_WITH_POLLING_REQUEST]: this.getDealDetailsWithPolling.bind(this),
      [WALLET_RESET_DEAL_NOTIFICATION_REQUEST]: this.dealerDealService.resetDealNotifications.bind(this.dealerDealService),
      [WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST]: this.dealerDealService.updateDealNotification.bind(this),
      [WALLET_GET_DEALS_WITH_DEALER_INFORMATION_REQUEST]: this.getDealerInformation.bind(this),
      [WALLET_PAYOFF_QUOTE_SERVICE_GET_LENDERS_REQUEST]: this.handlePayoffQuoteService.bind(this, WALLET_PAYOFF_QUOTE_SERVICE_GET_LENDERS_COMPLETED, 'getAllLenders'),
      [WALLET_PAYOFF_QUOTE_SERVICE_GET_SELECTED_LENDER_REQUEST]: this.handlePayoffQuoteService.bind(this, WALLET_PAYOFF_QUOTE_SERVICE_GET_SELECTED_LENDER_COMPLETED, 'getSearchOptionsByLenderName'),
      [WALLET_PAYOFF_QUOTE_SERVICE_SUBMIT_REQUEST]: this.handlePayoffQuoteService.bind(this, WALLET_PAYOFF_QUOTE_SERVICE_SUBMIT_COMPLETED, 'submitPayoffQuote'),
      [WALLET_VRS_OPTIONS_REQUEST]: this.getVrsOptions.bind(this),
      [WALLET_VRS_VEHICLE_VALUATION_REQUEST]: this.getVrsValuation.bind(this),
      [WALLET_GET_INVENTORY_PAYMENTS_REQUEST]: getInventoryPayments.bind(this),
      [WALLET_CREATE_ID_REQUEST]: this.getWalletIdCreation.bind(this),
      [UDS_CONFIG_REQUEST]: this.getCalculatorConfig.bind(this),
      [UDS_CONFIG_UPDATE_REQUEST]: this.updateCalculatorConfig.bind(this),
      [UDS_OPEN_SDP_REQUEST]: this.handleOpenSdpRequest.bind(this)
    });

    /**
     * Flag indicating if payments info has been loaded
     * @type {Boolean}
     */
    this.isPaymentsInfoLoaded = false;

    /**
     * Flag indicating if payments info is currently loading
     * @type {Boolean}
     */
    this.isPaymentsInfoLoading = false;
  }

  /**
   * Connects event handlers to the event bus
   * @param {Object} eventMap - Map of event names to handler functions
   * @private
   */
  connectEventBus(eventMap) {
    Object.entries(eventMap).forEach(_ref => {
      let [eventName, callback] = _ref;
      if (eventName && typeof callback === 'function') {
        eventBus.on(eventName, callback);
      }
    });
  }

  /**
   * Updates the payments information with new data
   * @param {Object} data - New payments information data to be merged with existing data
   */
  setPaymentsInfo(data) {
    this.paymentsInfo = buildPaymentsInfoForWalletManager(this.paymentsInfo, data);
  }

  /**
   * Updates the UDS (Unified Deal Starter) configuration with new data
   * @param {Object} data - New configuration data to be merged with existing config
   */
  setUDSConfig(data) {
    this.calcConfig = buildUDSConfigForWalletManager(this.calcConfig, data);
  }

  /**
   * Fetches wallet configuration from the server
   * @returns {Promise<Object>} The wallet configuration
   */
  async fetchWalletConfig() {
    let walletConfig = {};
    try {
      walletConfig = await this.fetchers.WalletConfigFetcher.fetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching wallet config values:', error);
    }
    return walletConfig;
  }

  /**
   * Loads payment information data if not already loaded
   * @param {Object} options - Loading options
   * @param {Boolean} [options.isLoggedIn] - Whether the user is logged in
   * @param {Object} [options.prevPaymentsInfo={}] - Previous payment information
   * @param {Object} [options.interestRates] - Interest rates data
   * @returns {Promise<void>}
   */
  async loadPaymentsInfo() {
    let {
      isLoggedIn,
      prevPaymentsInfo = {},
      interestRates
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    if (!this.isPaymentsInfoLoaded && !this.isPaymentsInfoLoading) {
      // set the flag to true to prevent multiple triggers
      this.isPaymentsInfoLoading = true;
      const response = await hydrateWallet({
        walletStorageService: this.walletStorageService,
        prevPaymentsInfo,
        myWalletConfigs: await this.fetchWalletConfig(),
        walletDefaults: this.walletDefaults
      });
      const {
        paymentInfo
      } = response;

      // when valid paymentInfo is returned from hydrateWalletPaymentsInfo, notify apps
      // checking for lastUpdated to support wallets that predate createdDate
      if ((paymentInfo?.createdDate || paymentInfo?.lastUpdated) && paymentInfo?.isPaymentsInfoLoaded) {
        this.setPaymentsInfo({
          ...paymentInfo
        });
        this.isPaymentsInfoLoaded = true;
        this.isPaymentsInfoLoading = false;

        // WALLET MANAGER TODO: Does Wallet Manager need to keep track of this?
        this.interestRates = {
          ...interestRates
        };
        debugWallet('Wallet data sucessfully loaded:', response);
        eventBus.emit(WALLET_ENSURE_PAYMENTS_INFO_COMPLETED, response);
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to load Payments Info');
      }
    }
  }

  /**
   * Emits the current wallet payments information if loaded
   */
  getWalletPaymentsInformation() {
    if (this.isPaymentsInfoLoaded) {
      eventBus.emit(WALLET_GET_PAYMENTS_INFORMATION_COMPLETED, this.paymentsInfo);
    }
  }

  /**
   * Emits the current wallet initialization status
   */
  getInitializationStatus() {
    eventBus.emit(WALLET_GET_INITIALIZATION_STATUS_COMPLETED, {
      isInitialized: this.isPaymentsInfoLoaded
    });
  }

  /**
   * Handles updates to payment information
   * @param {Object} eventPayload - Event payload with update information
   * @param {Object} [eventPayload.payload] - Data payload for the update
   * @param {String} [eventPayload.paymentOption] - Payment option being updated
   * @param {String} [eventPayload.budgetSummaryType] - Type of budget summary
   * @param {Object} [eventPayload.myWalletConfig] - Wallet configuration
   * @param {Object} [eventPayload.userInputData] - User input data
   * @returns {Promise<void>}
   */
  async handlePaymentsInfoUpdate(eventPayload) {
    const {
      payload,
      paymentOption,
      budgetSummaryType,
      myWalletConfig,
      userInputData
    } = eventPayload || {};
    const response = await updatePaymentsInfo({
      walletStorageService: this.walletStorageService,
      currentPaymentsInfo: this.paymentsInfo,
      payload,
      paymentOption,
      budgetSummaryType,
      myWalletConfig,
      paymentCalculationFetcher: this.fetchers.PaymentCalculationFetcher
    });
    if (!response?.success) {
      eventBus.emit(WALLET_SET_ERROR_ALERT_REQUEST, {
        hasError: true
      });
    }
    if (response?.updated && response?.updatedPaymentsInfo) {
      this.setPaymentsInfo(response.updatedPaymentsInfo);
    }
    // send a response event
    eventBus.emit(WALLET_UPDATE_PAYMENTS_INFO_COMPLETED, {
      ...response,
      // send additional custom data received from the app
      userInputData
    });
  }

  /**
   * Updates penciling offer price in payments info
   * @param {Object} payload - Payload containing offer price
   */
  updatePencilingOfferPrice(payload) {
    if (payload?.offerPrice && this.paymentsInfo) {
      this.paymentsInfo = {
        ...this.paymentsInfo,
        penciling: {
          ...this.paymentsInfo.penciling,
          offerPrice: payload?.offerPrice
        }
      };
    }
  }

  /**
   * Retrieves and processes trade-in data
   * @param {Object} options - Trade-in options
   * @param {String} [options.pixAllId] - PixAll ID for user identification
   * @param {Object} [options.myWalletConfigs] - Wallet configurations
   * @param {Object} [options.cbhTradeIns] - CBH trade-in data
   * @param {Boolean} [options.blockTradeInFetch] - Flag to block trade-in fetching
   * @returns {Promise<void>}
   */
  async getTradeIns() {
    let {
      pixAllId,
      myWalletConfigs,
      cbhTradeIns,
      blockTradeInFetch
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const tradeInsData = await buildKbbTradeIns({
      tradeInFetcher: this.fetchers.TradeInFetcher,
      pixAllId,
      myWalletConfigs,
      walletStorageService: this.walletStorageService,
      paymentsInfo: this.paymentsInfo,
      cbhTradeIns,
      blockTradeInFetch
    });
    debugWallet('KBB Trade-ins (Formatted): ', tradeInsData);
    if (tradeInsData?.hasNewTradeIn) {
      this.setPaymentsInfo({
        tradeIn: {
          ...this.paymentsInfo.tradeIn,
          valuationHistory: tradeInsData?.valuationHistory,
          hasNewTradeIn: true
        }
      });
    }
    eventBus.emit(WALLET_GET_KBB_TRADE_INS_COMPLETED, tradeInsData);
  }

  /**
   * Updates wallet storage with new data
   * @param {Object} options - Update options
   * @param {String} [options.key] - Key for the data being updated
   * @param {Object} [options.payload] - Data payload
   * @param {Boolean} [options.shouldSetLastUpdated] - Whether to update the lastUpdated timestamp
   * @param {String} [options.actionCode=''] - Action code for tracking the update
   * @returns {Promise<void>}
   */
  async updateWalletStorageWithData() {
    let {
      key,
      // TODO: this may not be needed
      payload,
      shouldSetLastUpdated,
      actionCode = ''
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const {
      newPaymentsInfo,
      success
    } = await updateWalletStorage({
      walletStorageService: this.walletStorageService,
      currentPaymentsInfo: this.paymentsInfo,
      payload,
      shouldSetLastUpdated,
      actionCode
    });
    if (!success) {
      eventBus.emit(WALLET_SET_ERROR_ALERT_REQUEST, {
        hasError: true
      });
    }

    // ..update wallet storage
    eventBus.emit(WALLET_UPDATE_WALLET_STORAGE_COMPLETED, {
      key,
      paymentsInfo: {
        newPaymentsInfo
      }
    });
  }

  /**
   * Retrieves deal details for a wallet
   * @param {Object} options - Request options
   * @param {String} [options.walletId] - Wallet ID to retrieve deals for
   * @returns {Promise<void>}
   */
  async getDealDetails() {
    let {
      walletId
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const response = await this.dealerDealService.getDealDetails(walletId);
    this.dealDetails = response;
    eventBus.emit(WALLET_GET_DEAL_DETAILS_COMPLETED, response);
  }

  /**
   * Polls for deal details with a specified interval
   * @param {Object} options - Polling options
   * @param {String} [options.walletId] - Wallet ID to retrieve deals for
   * @param {String} [options.dealXgId] - Deal ID to look for in the response
   * @returns {Promise<void>}
   */
  async getDealDetailsWithPolling() {
    let {
      walletId,
      dealXgId
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let n = 0;

    // poll every 3s for 1m to allow for round-trip deal latency
    const pollForDetails = setInterval(async () => {
      const response = await this.dealerDealService.getDealDetails(walletId);
      this.dealDetails = response;
      const deal = response.filter(d => d.id === dealXgId);
      if (deal.length || n === 20) {
        eventBus.emit(WALLET_GET_DEAL_DETAILS_COMPLETED, response);
        if (n === 20 && !deal.length) {
          // eslint-disable-next-line no-console
          console.log(`[Wallet Manager] Polling timed out for deal ${dealXgId} in wallet ${walletId}`);
        }
        clearInterval(pollForDetails);
      }
      n += 1;
    }, 3000);
  }

  /**
   * Retrieves dealer information for the specified deal details
   * @param {Object} dealDetails - Deal details to augment with dealer information
   * @returns {Promise<void>}
   */
  async getDealerInformation(dealDetails) {
    const dealIds = Object.keys(dealDetails);
    const dealWithListing = {};
    await Promise.all(dealIds.map(async dealId => {
      const {
        listing,
        owner
      } = await buildDealerInfomation({
        vin: dealDetails[dealId]?.vehicle?.vin,
        ownerDetailsFetcher: this.fetchers.ownerDetailsFetcher
      });
      dealWithListing[dealId] = {
        ...dealDetails[dealId],
        dealer: owner,
        vehicle: {
          ...dealDetails[dealId]?.vehicle,
          ...listing
        }
      };
    }));
    if (Object.keys(dealWithListing).length) {
      debugWallet('Wallet Dealer Infomation data sucessfully loaded:', dealWithListing);
      eventBus.emit(WALLET_GET_DEALS_WITH_DEALER_INFORMATION_COMPLETED, dealWithListing);
    }
  }

  /**
   * Handles payoff quote service actions
   * @param {String} completeEvent - Event to emit when the action completes
   * @param {String} action - Service action to perform
   * @param {...*} args - Arguments for the service action
   * @returns {Promise<void>}
   */
  async handlePayoffQuoteService(completeEvent, action) {
    if (typeof this.payoffQuoteService[action] === 'function') {
      for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
        args[_key - 2] = arguments[_key];
      }
      // pass extra data to the service
      const response = await this.payoffQuoteService[action](...args);
      eventBus.emit(completeEvent, response);
    }
  }

  /**
   * Handles retrieving ICO trade-in data
   * @param {Object} options - Request options
   * @param {String} options.pixAllId - PixAll ID for user identification
   * @param {Object} options.myWalletConfigs - Wallet configurations
   * @returns {Promise<void>}
   */
  async handleGetIcoTradeIns(_ref2) {
    let {
      pixAllId,
      myWalletConfigs
    } = _ref2;
    if (pixAllId && myWalletConfigs) {
      // fetch offers
      const offers = await getIcoOffers({
        icoFetcher: this.fetchers.IcoFetcher,
        pixAllId
      });
      debugWallet('ICO Offers: ', offers);
      if (offers?.length > 0) {
        const offersWithVrsValues = await getVrsReferenceData({
          vrsServices: this.vrsService,
          offers
        });
        const icoTradeInOffers = {
          ico: offersWithVrsValues
        };

        // validate payments info.
        validatePaymentsInfo(this.paymentsInfo, myWalletConfigs);

        // update wallet storage
        updateWalletStorage({
          walletStorageService: this.walletStorageService,
          currentPaymentsInfo: this.paymentsInfo,
          payload: {
            tradeIn: icoTradeInOffers
          },
          shouldSetLastUpdated: false
        });

        // set payments info
        this.setPaymentsInfo({
          tradeIn: {
            ...this.paymentsInfo.tradeIn,
            ...icoTradeInOffers
          }
        });

        // send completed event
        eventBus.emit(WALLET_GET_ICO_TRADE_INS_COMPLETED, icoTradeInOffers);
      }
    }
  }

  /**
   * Retrieves VRS options based on the specified action
   * @param {Object} options - Request options
   * @param {String} options.action - VRS service action to perform
   * @param {Object} options.data - Data for the VRS action
   * @returns {Promise<void>}
   */
  async getVrsOptions(_ref3) {
    let {
      action,
      data
    } = _ref3;
    if (typeof this.vrsService[action] === 'function') {
      const response = await this.vrsService[action](data);
      eventBus.emit(WALLET_VRS_OPTIONS_COMPLETED, {
        action,
        data: response
      });
    }
  }

  /**
   * Retrieves vehicle valuation from VRS
   * @param {Object} data - Vehicle data for valuation
   * @param {String} [data.yearId] - Vehicle year ID
   * @param {String} [data.makeId] - Vehicle make ID
   * @param {String} [data.modelId] - Vehicle model ID
   * @param {String} [data.trimId] - Vehicle trim ID
   * @param {Number} [data.mileage] - Vehicle mileage
   * @param {String} [data.condition] - Vehicle condition
   * @param {String} [data.zip] - ZIP code for location-specific valuation
   * @returns {Promise<void>}
   */
  async getVrsValuation() {
    let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const {
      yearId,
      makeId,
      modelId,
      trimId,
      mileage,
      condition,
      zip
    } = data;
    const vrsVehicleDetails = await this.vrsService.getVehicleId({
      yearId,
      makeId,
      modelId,
      trimId
    });
    const vrsVehicleId = vrsVehicleDetails?.items?.[0]?.vehicleId;
    const response = {
      payload: data,
      vrsVehicleDetails,
      vrsVehicleId
    };
    if (vrsVehicleId) {
      // Use the vehicleId to fetch a valuation, params are vehicleId, condition, mileage, zip
      const valuationResponse = await this.vrsService.getVehicleValuation({
        configuration: {
          vehicleId: vrsVehicleId
        },
        zipCode: zip,
        mileage,
        condition
      });
      response.valuationResponse = {
        ...valuationResponse
      };
    } else {
      response.error = true;
    }
    eventBus.emit(WALLET_VRS_VEHICLE_VALUATION_COMPLETED, response);
  }

  /**
   * Creates or retrieves a wallet ID
   * @returns {Promise<String>} The wallet ID
   */
  async getWalletIdCreation() {
    let walletId = this.walletId ?? this.walletStorageService.getWalletId() ?? null;
    if (!walletId) {
      walletId = await createWalletId({
        walletStorageService: this.walletStorageService,
        paymentInfo: this.paymentsInfo
      });
    }
    this.walletId = walletId;
    eventBus.emit(WALLET_CREATE_ID_COMPLETED, walletId);
    return walletId;
  }

  /**
   * Retrieves or builds calculator configuration
   * @param {Object} options - Configuration options
   * @param {Boolean} [options.buildConfig=false] - Whether to build a new configuration
   * @param {Object} [options.externalConfig={}] - External configuration to modify
   * @param {Object} [options.listing] - Vehicle listing data
   * @param {Object} [options.payments] - Payment data
   * @param {Object} [options.interestRates] - Interest rates data
   * @returns {Promise<void>}
   */
  async getCalculatorConfig(_ref4) {
    let {
      buildConfig = false,
      externalConfig = {},
      listing,
      payments,
      interestRates,
      isEcommHyundai = false,
      isSingleColumn
    } = _ref4;
    let config = {};
    const paymentCalculationFetcher = this.fetchers.PaymentCalculationFetcher;
    const drSettingsFetcher = this.fetchers.DrSettingsFetcher;
    const paymentIncentivesFetcher = this.fetchers.PaymentIncentivesFetcher;
    const startTime = getFormattedTime();
    debugWallet('UDS', 'Config Request', {
      externalConfig,
      listing
    });
    if (buildConfig) {
      // Build the config for FYC
      config = await buildCalculatorConfig(listing, payments, interestRates, paymentCalculationFetcher, paymentIncentivesFetcher, isEcommHyundai, isSingleColumn);
    } else if (Object.keys(externalConfig).length > 0) {
      // Update/modify the config for Syndication
      config = await modifyExternalConfig(externalConfig, listing, paymentCalculationFetcher, drSettingsFetcher, paymentIncentivesFetcher, payments = this.paymentsInfo);
    }
    config = {
      ...config,
      walletZip: this.paymentsInfo?.zip || payments?.zip,
      ...(Object.keys(config).length && config?.ecommerce && {
        // Analysis the load time only on syndication
        udsInitializationTime: startTime
      })
    };
    this.setUDSConfig(config);
    debugWallet('UDS', 'Config Request Complete', config);
    eventBus.emit(UDS_CONFIG_COMPLETED, config);
  }

  /**
   * Updates calculator configuration for syndication
   * @param {Object} options - Update options
   * @param {Object} [options.payments={}] - Payment data
   * @param {Object} [options.config={}] - Configuration to update
   * @returns {Promise<void>}
   */
  async updateCalculatorConfig(_ref5) {
    let {
      payments = {},
      config = {}
    } = _ref5;
    if (Object.keys(payments).length > 0 && Object.keys(config).length > 0) {
      debugWallet('UDS', 'Config Update Request', config);
      let UpdatedConfig = await updateCalculatorConfig(payments, config);
      UpdatedConfig = {
        ...UpdatedConfig,
        walletZip: payments?.zip
      };
      this.setUDSConfig(UpdatedConfig);
      debugWallet('UDS', 'Config Update Request Complete', UpdatedConfig);
      eventBus.emit(UDS_CONFIG_UPDATE_COMPLETED, UpdatedConfig);
    }
  }

  /**
   * Handles opening the Shopper Deal Platform (SDP)
   * @param {Object} options - SDP options
   * @param {Object} [options.hooks={}] - Callback hooks
   * @param {Boolean} [options.isMobile=false] - Whether the platform is mobile
   * @param {Object} [options.pixall={}] - PixAll data
   * @param {String} [options.route=''] - Route to open
   * @param {Object} [options.shopper={}] - Shopper data
   * @param {String} [options.brand='atc'] - Brand identifier
   * @param {Object} [options.listing={}] - Vehicle listing data
   * @param {String} [options.listedPrice=''] - Listed price
   * @param {Object} [options.paymentsInfo=null] - Payment information override
   * @returns {Promise<void>}
   */
  async handleOpenSdpRequest(_ref6) {
    let {
      hooks = {},
      isMobile = false,
      pixall = {},
      route = '',
      shopper = {},
      brand = 'atc',
      listing = {},
      listedPrice = '',
      paymentsInfo = null
    } = _ref6;
    const walletId = this.walletId ?? (await this.getWalletIdCreation());
    const {
      onDRLoaded
    } = hooks;
    const {
      injectSDPScript,
      waitForAccelerate
    } = digitalOverlayHelpers;
    let dealsArray = [];
    let selectedDeal;
    const effectivePaymentsInfo = paymentsInfo || this.paymentsInfo;
    if (this.dealDetails) {
      dealsArray = Object.values(this.dealDetails);
      const selectedVin = listing?.vin ?? this.calcConfig?.listing?.vin;
      if (dealsArray.length) {
        selectedDeal = dealsArray.find(deal => deal.vehicle?.vin === selectedVin);
      }
    }
    const dealXgId = selectedDeal?.id ?? null;
    let sponsor;
    switch (this?.calcConfig?.ecommerce) {
      case 'BOA':
        sponsor = this?.calcConfig?.sponsor;
        break;
      case 'Accelerate':
        sponsor = 'ddc';
        break;
      default:
        sponsor = brand;
        break;
    }
    const init = await buildInitObject(isMobile, route, hooks, sponsor, this.calcConfig, effectivePaymentsInfo, walletId, pixall, dealXgId, shopper, listing, listedPrice);
    debugWallet('UDS', 'Open SDP Request with init Object:', init);
    if (!window.accelerate) {
      await injectSDPScript();
      await waitForAccelerate();
    }
    if (window.accelerate) {
      if (route) {
        try {
          window.accelerate.openShopperPlatform(init);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Error launching the SDP widget: ', error);
          onDRLoaded(error, init?.offer);
        }
      } else {
        debugWallet('UDS', 'SDP Open Request Failed due to route missing from the payload');
      }
    } else {
      debugWallet('UDS', 'SDP Open Request Failed due to accelerate missing from the window object');
    }
  }
}
export { WalletManager as default };
